import React, { useCallback, useMemo, useState } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, UseFormSetValue, UseFormWatch, useFormState } from 'react-hook-form';

import { Box, Grid, MenuItem, TextField, Typography } from '@mui/material';

import { useGetEmployee } from '~/hooks/apis/business-traveller/useGetEmployee';
import { useGetServiceFees } from '~/hooks/apis/business-traveller/useGetServiceFees';

import {
  CUSTOM_SERVICE_FEE_DROPDOWN_OPTION,
  getCustomOfferServiceFeeOptions,
} from '~/utils/businessTraveller/serviceFee';

const NO_SELECTION_VALUE = '';

interface Props {
  userId: string;
  control: Control<CustomOfferFormValues>;
  setValue: UseFormSetValue<CustomOfferFormValues>;
  watch: UseFormWatch<CustomOfferFormValues>;
  isSubmitting: boolean;
  /** Locks all fields when the offer already has a paid order. */
  disabled?: boolean;
}

export default function CustomOfferBusinessTravellerServiceFeeSection({
  userId,
  control,
  setValue,
  watch,
  isSubmitting,
  disabled,
}: Props) {
  const itemType = watch('item.type');
  const itemCountry = watch('item.country');
  const flightLegs = watch('item.flight_legs');
  const flightType = watch('item.flight_type');
  const selectedFee = watch('payment_metadata.lebt_service_fee');
  const { errors } = useFormState({ control, name: 'payment_metadata.lebt_service_fee' });
  const amountErrorMessage = errors.payment_metadata?.lebt_service_fee?.amount?.message;

  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const { data: employee } = useGetEmployee(userId);
  const businessId = employee?.businessId ?? '';

  const { data: serviceFeesResponse, isLoading: isLoadingFees } = useGetServiceFees(businessId);

  const dropdownOptions = useMemo(
    () =>
      getCustomOfferServiceFeeOptions(serviceFeesResponse?.result ?? [], itemType, {
        country: itemCountry,
        flightLegs,
        flightType,
      }),
    [serviceFeesResponse, itemType, itemCountry, flightLegs, flightType],
  );

  // Match the saved fee to a dropdown option.
  const selectedIndex = useMemo(() => {
    if (isCustomSelected) {
      return dropdownOptions.findIndex((option) => !!option.isCustomServiceFee);
    }
    if (!selectedFee) {
      return -1;
    }
    return dropdownOptions.findIndex((option) => {
      // A zero-amount selection only ever maps to the no-fee row.
      if (selectedFee.amount === 0) {
        return !!option.isNoServiceFee;
      }
      // Paid fees match on their discriminators and never resolve to the no-fee row.
      return (
        !option.isNoServiceFee &&
        option.source === selectedFee.source &&
        option.type === selectedFee.charge_type &&
        option.destination === selectedFee.destination &&
        option.product === selectedFee.product &&
        option.conciergeMode === selectedFee.concierge_mode
      );
    });
  }, [dropdownOptions, isCustomSelected, selectedFee]);

  const selectedKey = selectedIndex >= 0 ? String(selectedIndex) : NO_SELECTION_VALUE;
  const selectedDropdownOption = selectedIndex >= 0 ? dropdownOptions[selectedIndex] : undefined;

  const handleDropdownChange = useCallback(
    (newKey: string) => {
      if (newKey === NO_SELECTION_VALUE) {
        setValue('payment_metadata.lebt_service_fee', undefined);
        return;
      }

      const index = parseInt(newKey, 10);
      const option = dropdownOptions[index];
      if (!option) {
        return;
      }

      setIsCustomSelected(!!option.isCustomServiceFee);
      if (option.isCustomServiceFee) {
        setValue('payment_metadata.lebt_service_fee', undefined);
        return;
      }

      setValue('payment_metadata.lebt_service_fee', {
        source: option.source,
        charge_type: option.type,
        destination: option.destination,
        product: option.product,
        concierge_mode: option.conciergeMode,
        amount: option.amount,
      });
    },
    [dropdownOptions, setValue],
  );

  const handleAmountChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      setValue(
        'payment_metadata.lebt_service_fee',
        {
          ...(selectedFee ?? {
            source: CUSTOM_SERVICE_FEE_DROPDOWN_OPTION.source,
            charge_type: CUSTOM_SERVICE_FEE_DROPDOWN_OPTION.type,
            destination: CUSTOM_SERVICE_FEE_DROPDOWN_OPTION.destination,
            product: CUSTOM_SERVICE_FEE_DROPDOWN_OPTION.product,
            concierge_mode: CUSTOM_SERVICE_FEE_DROPDOWN_OPTION.conciergeMode,
          }),
          amount: Number.isFinite(parsed) ? parsed : undefined,
        },
        { shouldValidate: true },
      );
    },
    [selectedFee, setValue],
  );

  const fieldsDisabled = isSubmitting || !!disabled;
  const amountDisabled = fieldsDisabled || !selectedDropdownOption || !!selectedDropdownOption.isNoServiceFee;

  return (
    <Box mt={2}>
      <Typography variant="h6">Service fee</Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        The service fee is charged separately to the customer on top of the offer price.
      </Typography>

      <Grid container columns={6} spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Controller
            name="payment_metadata.lebt_service_fee"
            control={control}
            render={({ fieldState: { error } }) => (
              <TextField
                value={selectedKey}
                onChange={(e) => handleDropdownChange(e.target.value)}
                label="Pre-defined service fee"
                helperText={error?.message ?? (isLoadingFees ? 'Loading service fees…' : ' ')}
                error={!!error}
                disabled={fieldsDisabled || isLoadingFees}
                required
                select
                fullWidth
                data-testid="lebt-service-fee-dropdown"
              >
                <MenuItem value={NO_SELECTION_VALUE} disabled>
                  <Typography component="span" sx={{ fontStyle: 'italic' }}>
                    Select a service fee
                  </Typography>
                </MenuItem>
                {dropdownOptions.map((option, index) => (
                  <MenuItem key={index} value={String(index)}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid size={{ xs: 6, md: 2 }}>
          <TextField
            value={selectedFee?.amount ?? ''}
            onChange={(e) => handleAmountChange(e.target.value)}
            label="Service fee amount"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            disabled={amountDisabled}
            fullWidth
            error={!!amountErrorMessage}
            helperText={
              amountErrorMessage ??
              (selectedDropdownOption?.isNoServiceFee
                ? 'No service fee selected.'
                : selectedDropdownOption
                  ? 'Editable'
                  : 'Pick a service fee first.')
            }
            data-testid="lebt-service-fee-amount"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
